import api from './api';

/**
 * Site Analytics Service
 * — wrapper روی /api/admin/analytics/*
 * — api.get<T> خود لایه‌ی outer {status,data} را باز می‌کند و T را برمی‌گرداند.
 */

export type TimeRange = { from?: string; to?: string };

export interface OverviewMetrics {
  sessions: number;
  visitors: number;
  newVisitors: number;
  returningVisitors: number;
  pageViews: number;
  uniquePageViews: number;
  bounceRate: number;
  /** میانگین روی همه‌ی سشن‌های واقعی (bounce = ۰ ثانیه) */
  avgSessionDurationSec: number;
  /** میانگین فقط روی سشن‌های درگیر (چند-صفحه‌ای) */
  avgEngagedDurationSec?: number;
  pagesPerSession: number;
  eventCount: number;
  /** فقط رویدادهای هدف (خرید dedupe شده، ثبت‌نام، …) */
  conversions: number;
  /** سفارش‌های PAID واقعی در بازه */
  purchases?: number;
  /** درآمد واقعی (تومان) از سفارش‌های PAID */
  revenue?: number;
  avgOrderValue?: number;
  /** خرید ÷ سشن — یک رقم اعشار */
  purchaseConversionPct?: number;
}

export interface OverviewResponse {
  range: { from: string; to: string };
  metrics: OverviewMetrics;
  change: {
    sessions: number;
    visitors: number;
    pageViews: number;
    bounceRate: number;
    avgSessionDuration: number;
    purchases?: number;
    revenue?: number;
  };
  timeSeries: Array<{
    date: string;
    sessions: number;
    visitors: number;
    pageViews: number;
    newVisitors: number;
    bounces: number;
  }>;
}

export interface RealtimeResponse {
  activeNow: { visitors: number; sessions: number };
  activeLast30Min: number;
  byDevice: Array<{ key: string; count: number }>;
  byCountry: Array<{ key: string; count: number }>;
  byMedium: Array<{ key: string; count: number }>;
  recentEvents: Array<{
    path: string;
    title?: string;
    ts: string;
    country?: string;
    deviceType?: string;
    medium?: string;
  }>;
  recentActivity?: Array<{
    name: string;
    category?: string;
    path?: string;
    ts: string;
    country?: string;
    deviceType?: string;
    medium?: string;
    value?: number;
  }>;
  topActivePages: Array<{ path: string; views: number; activeVisitors: number }>;
  last24Hours: Array<{ hour: string; sessions: number; visitors: number; pageViews: number; events: number }>;
  /** نبض تجاری زنده — خرید/درآمد ۳۰ دقیقه‌ی اخیر (dedupe روی orderId) */
  last30Min?: { purchases: number; revenue: number };
}

export interface DimensionRow {
  value: string;
  title?: string;
  sessions: number;
  visitors: number;
  pageViews: number;
  /** فقط ردیف‌های Top Pages رفتار (از AnalyticsPageView) این فیلد را دارند */
  uniquePageViews?: number;
  bounceRate: number;
  avgDuration: number;
}

/** ردیف صفحات ورود در «رفتار» — session-based؛ pageViews/uniquePageViews دیگر ارسال نمی‌شود */
export interface LandingRow {
  value: string;
  sessions: number;
  visitors: number;
  bounceRate: number;
  avgDuration: number;
}

/** تفکیک دقیق منبع ورودی (بله/روبیکا/ایتا/تلگرام/گوگل/…) — label ها از بک‌اند فارسی می‌آیند */
export interface SourceRow {
  source: string;
  label: string;
  channel: string;
  channelLabel: string;
  sessions: number;
  visitors: number;
  pageViews: number;
  bounceRate: number;
  avgDuration: number;
}

export interface ChannelRow {
  channel: string;
  label: string;
  sessions: number;
  visitors: number;
  pageViews: number;
  bounceRate: number;
  avgDuration: number;
}

export interface CityRow {
  value: string;
  sessions: number;
  visitors: number;
}

export interface HeatmapCell {
  dayOfWeek: number; // 0=یکشنبه
  hour: number; // 0-23
  count: number;
}

export interface AttributionRow {
  medium: string;
  conversions: number;
  /** تعداد خرید واقعی (dedupe بر اساس orderId) */
  purchases?: number;
  /** درآمد فقط از رویدادهای purchase (نه add_to_cart/checkout/...) */
  revenue: number;
}

export interface AcquisitionResponse {
  range: { from: string; to: string };
  byMedium: DimensionRow[];
  topReferrers: DimensionRow[];
  topUtm: DimensionRow[];
  topLanding: DimensionRow[];
  topSocial: Array<{ socialNetwork: string; sessions: number; visitors: number }>;
  bySource: SourceRow[];
  byChannel: ChannelRow[];
  attribution?: { firstTouch: AttributionRow[]; lastTouch: AttributionRow[] };
}

export interface SavedFunnel {
  _id: string;
  name: string;
  description?: string;
  steps: string[];
  createdAt: string;
}

export interface AudienceResponse {
  range: { from: string; to: string };
  byCountry: DimensionRow[];
  byDevice: DimensionRow[];
  byBrowser: DimensionRow[];
  byOS: DimensionRow[];
  byLanguage: Array<{ value: string; count: number }>;
  byCity?: CityRow[];
  authStats: { authenticated: number; anonymous: number };
  /** 🆕 جدید vs بازگشتی در بازه */
  newVsReturning?: { newVisitors: number; returningVisitors: number };
}

export interface BehaviorResponse {
  range: { from: string; to: string };
  topPages: DimensionRow[];
  topLanding: LandingRow[];
  topExit: Array<{ path: string; count: number; views?: number; exitRatePct?: number }>;
  top404: Array<{ path: string; count: number }>;
  scrollDepth: Array<{ path: string; avgScroll: number; views: number }>;
  avgTime: Array<{ path: string; avgTime: number; views: number }>;
  heatmap?: HeatmapCell[];
}

export interface FunnelStep {
  step: string;
  visitors: number;
  dropOffPct: number;
  /** 🆕 median زمانِ رسیدن از مرحله‌ی قبل (ثانیه) — null برای مرحله‌ی اول */
  medianLagSec?: number | null;
}
export interface FunnelData {
  range: { from: string; to: string };
  steps: FunnelStep[];
  totalConversionRate: number;
}

export interface RetentionResponse {
  weeks: number;
  cohorts: Array<{
    cohort: string;
    size: number;
    /** pct === null یعنی آن هفته هنوز برای این cohort نرسیده است */
    retention: Array<{ week: number; visitors: number; pct: number | null }>;
  }>;
}

export interface VitalsSummary {
  avgLcp: number | null;
  p75Lcp: number | null;
  p95Lcp: number | null;
  avgInp: number | null;
  p75Inp: number | null;
  avgCls: number | null;
  p75Cls: number | null;
  avgFcp: number | null;
  avgTtfb: number | null;
  /** 🆕 سهم نمونه‌های «خوب» طبق آستانه‌های گوگل (٪) */
  goodLcpPct?: number | null;
  goodInpPct?: number | null;
  goodClsPct?: number | null;
  samples: number;
}

export interface PerformanceResponse {
  range: { from: string; to: string };
  overall: VitalsSummary | null;
  perPage: Array<VitalsSummary & { path: string }>;
  jsErrors: Array<{ name: string; path?: string; properties?: any; ts: string }>;
  /** 🆕 گروه‌بندی خطاها بر اساس نام+پیام */
  errorGroups?: Array<{ name: string; message: string; count: number; visitors: number; lastAt: string; lastPath?: string }>;
  samplesTotal?: number;
}

export interface Campaign {
  _id: string;
  name: string;
  code: string;
  utmSource?: string;
  utmMedium?: string;
  utmTerm?: string;
  utmContent?: string;
  destinationPath: string;
  description?: string;
  isActive: boolean;
  clicks: number;
  /** کلیک بات/کرالر (پیش‌نمایش پیام‌رسان‌ها) — جدا از کلیک انسانی */
  botClicks?: number;
  /** کلیک انسانی در بازهٔ انتخابی؛ null = کمپین قدیمی بدون شمارش روزانه */
  clicksInRange?: number | null;
  shortLink: string;
  targetUrl: string;
  createdAt: string;
  stats?: { sessions: number; visitors: number; conversions: number };
}

export interface CampaignInsights {
  campaign: Campaign | null;
  clicks: number;
  botClicks?: number;
  clicksInRange?: number | null;
  clicksSeries?: Array<{ date: string; clicks: number }>;
  code: string;
  range: { from: string; to: string };
  totals: {
    sessions: number;
    visitors: number;
    newVisitors: number;
    returningVisitors: number;
    pageViews: number;
    bounceRate: number;
    avgDurationSec: number;
    conversions: number;
    purchases: number;
    revenue: number;
    conversionRatePct: number;
    pagesPerSession: number;
  };
  timeSeries: Array<{ date: string; sessions: number; visitors: number; pageViews: number }>;
  byDevice: Array<{ value: string; count: number }>;
  byCountry: Array<{ value: string; count: number }>;
  bySource: Array<{ value: string; count: number }>;
  byMedium: Array<{ value: string; count: number }>;
  landingPages: Array<{ value: string; count: number }>;
  topPages: Array<{ path: string; pageViews: number; visitors: number }>;
  actions: Array<{ name: string; count: number; visitors: number; revenue: number }>;
  funnel: Array<{ step: string; visitors: number; dropOffPct: number }>;
  authStats: { authenticated: number; anonymous: number };
}

export type CampaignInput = Partial<
  Pick<
    Campaign,
    'name' | 'code' | 'utmSource' | 'utmMedium' | 'utmTerm' | 'utmContent' | 'destinationPath' | 'description' | 'isActive'
  >
>;

export interface SearchBreakdownRow {
  key: string;
  searches: number;
  zeroResults: number;
  clicked: number;
  purchased: number;
}

export interface SearchTopQuery {
  query: string;
  count: number;
  visitors: number;
  avgResults: number | null;
  zeroCount: number;
  clicks: number;
  ctrPct: number;
  carts: number;
  purchases: number;
  revenue: number;
  lastAt: string;
}

export interface SearchZeroQuery {
  query: string;
  count: number;
  visitors: number;
  lastAt: string | null;
  nowHasResults: boolean;
}

export interface SearchTopProduct {
  productId: string;
  name: string | null;
  slug: string | null;
  image: string | null;
  clicks: number;
  carts: number;
  purchases: number;
  revenue: number;
  topQueries: string[];
}

export interface SearchInsightsResponse {
  range: { from: string; to: string };
  totals: {
    searches: number; // جستجوی واقعی (burstهای جمع‌شده، نه رویداد خام)
    rawEvents: number;
    visitors: number;
    loggedInUsers: number;
    uniqueQueries: number;
    zeroResults: number;
    zeroResultPct: number;
    resultClicks: number;
    clickedSearches: number;
    clickThroughPct: number;
    cartedSearches: number;
    purchasedSearches: number;
    purchases: number;
    purchaseConversionPct: number;
    revenue: number;
  };
  funnel: Array<{ step: 'search' | 'with_results' | 'clicked' | 'carted' | 'purchased'; count: number }>;
  daily: Array<{ date: string; searches: number; visitors: number; zeroResults: number; clicks: number; purchases: number }>;
  topQueries: SearchTopQuery[];
  zeroResultQueries: SearchZeroQuery[];
  breakdowns: { bySource: SearchBreakdownRow[]; byDevice: SearchBreakdownRow[] };
  topProducts: SearchTopProduct[];
}

export type SearchOutcome = 'purchased' | 'carted' | 'clicked' | 'zero' | 'none';

export interface SearchLogRow {
  sid: string;
  ts: string;
  query: string;
  resultsCount: number | null;
  events: number;
  triggers: string[];
  source: string | null;
  user: { id: string; name: string | null; phoneNumber: string | null } | null;
  visitorId: string;
  deviceType: string | null;
  platform: string | null;
  path: string | null;
  click: { productId: string; name: string | null; slug: string | null; rank: number | null } | null;
  outcome: SearchOutcome;
  purchaseAmount: number | null;
  orderRef: string | null;
}

export interface SearchLogResponse {
  range: { from: string; to: string };
  page: number;
  limit: number;
  total: number;
  pages: number;
  rows: SearchLogRow[];
}

// ── «اپلیکیشن در برابر وب‌سایت» ─────────────────────────────────────────
// آینهٔ خروجی `analyticsQuery.getPlatforms` در بک‌اند.

/** پلتفرم ریزدانه — همان enum بک‌اند در constants/analyticsPlatforms.ts */
export type AnalyticsPlatform = 'web' | 'android' | 'ios' | 'mobile_app' | 'admin' | 'unknown';

/** سطلِ گزارشی: اپ / وب */
export type PlatformGroup = 'app' | 'web';

export interface PlatformActiveUsers {
  /** کاربران یکتای لاگین‌شده در ۱ / ۷ / ۳۰ روز اخیر (DAU / WAU / MAU) */
  d1: number;
  d7: number;
  d30: number;
}

export interface PlatformSummaryRow {
  group: PlatformGroup;
  label: string;
  sessions: number;
  /** بازدیدکنندهٔ یکتا شامل مهمان‌ها */
  visitors: number;
  /** حساب‌های لاگین‌شدهٔ یکتا؛ برای «چند نفر» عدد دقیق‌تر activeUsers است */
  users: number;
  pageViews: number;
  purchases: number;
  revenue: number;
  signups: number;
  bounceRate: number;
  avgSessionDurationSec: number;
  pagesPerSession: number;
  purchaseRatePct: number;
  activeUsers: PlatformActiveUsers;
}

export interface PlatformRow extends Omit<PlatformSummaryRow, 'group' | 'activeUsers'> {
  platform: AnalyticsPlatform;
  group: string;
  events: number;
}

export interface PlatformsResponse {
  range: { from: string; to: string };
  summary: PlatformSummaryRow[];
  shares: Array<{ group: PlatformGroup; label: string; sessionSharePct: number }>;
  byPlatform: PlatformRow[];
  trend: Array<{ date: string; app: number; web: number; appUsers: number; webUsers: number }>;
  userSplit: {
    appOnly: number;
    webOnly: number;
    both: number;
    known: number;
    /** درصد کاربرانی که اپ را استفاده می‌کنند (تنها یا در کنار وب) */
    appAdoptionPct: number;
  };
  activeUsers: Record<PlatformGroup, PlatformActiveUsers>;
  appVersions: Array<{ version: string; platform: string; label: string; sessions: number; users: number }>;
  appDevices: Array<{ value: string; sessions: number; visitors: number }>;
  appOsVersions: Array<{ os?: string; version: string; sessions: number }>;
  installSources: Array<{ value: string; sessions: number; users: number }>;
  topScreens: Record<PlatformGroup, Array<{ path: string; pageViews: number; visitors: number }>>;
  devices: Array<{ platform: AnalyticsPlatform; label: string; devices: number; users: number; activeLast30d: number }>;
}

const BASE = '/admin/analytics';

/**
 * silentForbidden: true → interceptor در api.ts روی 403 ریدایرکت به /unauthorized نمی‌کند؛
 * فراخوان‌گر فقط همان بخش را پنهان می‌کند (برای فراخوان‌های پس‌زمینه با پرمیشن جداگانه).
 */
export type RequestOpts = { silentForbidden?: boolean };

const siteAnalyticsService = {
  getOverview: (range?: TimeRange) =>
    api.get<OverviewResponse>(`${BASE}/overview`, { params: range }),

  getRealtime: () =>
    api.get<RealtimeResponse>(`${BASE}/realtime`),

  getAcquisition: (range?: TimeRange) =>
    api.get<AcquisitionResponse>(`${BASE}/acquisition`, { params: range }),

  getAudience: (range?: TimeRange) =>
    api.get<AudienceResponse>(`${BASE}/audience`, { params: range }),

  getPlatforms: (range?: TimeRange) =>
    api.get<PlatformsResponse>(`${BASE}/platforms`, { params: range }),

  getBehavior: (range?: TimeRange) =>
    api.get<BehaviorResponse>(`${BASE}/behavior`, { params: range }),

  getStandardFunnels: (range?: TimeRange) =>
    api.get<{ range: any; funnels: Record<string, FunnelData> }>(`${BASE}/funnels`, { params: range }),

  getCustomFunnel: (steps: string[], range?: TimeRange) =>
    api.get<FunnelData>(`${BASE}/funnels/custom`, {
      params: { ...range, steps: steps.join(',') },
    }),

  // ----- Saved funnels -----
  listSavedFunnels: (opts?: RequestOpts) =>
    api.get<SavedFunnel[]>(`${BASE}/saved-funnels`, { ...(opts || {}) } as any),
  createSavedFunnel: async (body: { name: string; description?: string; steps: string[] }): Promise<SavedFunnel> => {
    const r: any = await api.post<any>(`${BASE}/saved-funnels`, body);
    return (r && r.data) || r;
  },
  deleteSavedFunnel: async (id: string): Promise<any> => {
    const r: any = await api.delete<any>(`${BASE}/saved-funnels/${id}`);
    return (r && r.data) || r;
  },

  getRetention: (weeks: number = 8) =>
    api.get<RetentionResponse>(`${BASE}/retention`, { params: { weeks } }),

  getActivityStats: (opts?: RequestOpts) =>
    api.get<{ DAU: number; WAU: number; MAU: number; stickinessPct: number }>(
      `${BASE}/activity`,
      { ...(opts || {}) } as any
    ),

  getPerformance: (range?: TimeRange) =>
    api.get<PerformanceResponse>(`${BASE}/performance`, { params: range }),

  getMarketplaceInsights: (range?: TimeRange) =>
    api.get<any>(`${BASE}/marketplace`, { params: range }),

  getEngagementInsights: (range?: TimeRange) =>
    api.get<any>(`${BASE}/engagement`, { params: range }),

  // ----- جستجوهای کاربران -----
  getSearchInsights: (range?: TimeRange) =>
    api.get<SearchInsightsResponse>(`${BASE}/searches`, { params: range }),

  getSearchLog: (params: TimeRange & { page?: number; limit?: number; q?: string; result?: 'all' | 'zero' | 'has' }) =>
    api.get<SearchLogResponse>(`${BASE}/searches/log`, { params }),

  triggerAggregateDay: (date?: string) =>
    api.post<any>(`${BASE}/aggregate/day`, { date }),

  triggerAggregateHour: () =>
    api.post<any>(`${BASE}/aggregate/hour`),

  // ----- Campaigns (UTM links + per-campaign analytics) -----
  listCampaigns: (range?: TimeRange) =>
    api.get<Campaign[]>(`${BASE}/campaigns`, { params: range }),

  // post/delete در wrapper پوسته‌ی {status,data} را باز نمی‌کنند → دستی unwrap می‌کنیم.
  createCampaign: async (body: CampaignInput): Promise<Campaign> => {
    const r: any = await api.post<any>(`${BASE}/campaigns`, body);
    return (r && r.data) || r;
  },

  updateCampaign: (id: string, body: CampaignInput) =>
    api.put<Campaign>(`${BASE}/campaigns/${id}`, body),

  deleteCampaign: async (id: string): Promise<any> => {
    const r: any = await api.delete<any>(`${BASE}/campaigns/${id}`);
    return (r && r.data) || r;
  },

  getCampaignInsights: (code: string, range?: TimeRange) =>
    api.get<CampaignInsights>(`${BASE}/campaigns/${encodeURIComponent(code)}/insights`, { params: range }),
};

export default siteAnalyticsService;
